/**
 * The trailing score: the same scorecard, on the most recent CLOSED months that end at
 * the range's end month. Asana "Implement Trailing Score Calculation Enhancements",
 * 2026-10-06. MEASURE-RULES §10.8.
 *
 * It is not a new formula. It is `scorecard()` run on a different month window — every
 * person's figures pooled over those months, peer averages over the active people with
 * claims in them — so it reads on the same −3…+11 scale as the range score and the two
 * can sit side by side.
 *
 *  - The window ENDS at the range's end month, so stepping the range back steps the
 *    trailing score back with it. It does NOT start at the range's start: a one-month
 *    range still gets a three-month trailing score, which is the point of having one.
 *  - A month still running is never in it. If the range ends on an open month (somebody
 *    picked it), the window ends on the last closed month before it: an open month's
 *    ratios are noise (see `lastClosedMonth`), and a trailing figure is meant to be
 *    the steady one.
 *  - Fewer closed months than TRAILING_MONTHS → the window is what there is, and the
 *    label says so (the window's own span is shown, never "3 months").
 */

/** How many closed months the trailing score covers. One constant, for the meeting. */
export const TRAILING_MONTHS = 3

/**
 * The months the trailing score covers, oldest first: the last `n` closed months of the
 * bundle that are on or before `end`. `end` null means the bundle's last month.
 */
export function trailingWindow(
  allMonths: readonly string[],
  openMonths: readonly string[],
  end: string | null,
  n: number = TRAILING_MONTHS,
): string[] {
  const open = new Set(openMonths)
  const closed = allMonths.filter((m) => !open.has(m) && (end === null || m <= end))
  return n > 0 ? closed.slice(-n) : []
}

/** "Trailing 3-mo" — named off the constant, so changing it renames every label. */
export const TRAILING_LABEL = `Trailing ${TRAILING_MONTHS}-mo`
